import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { RefreshCw, Package, CheckCircle2, XCircle, Clock, Mail } from 'lucide-react';

const ReturnPolicyPage = () => {
  const { currentLanguage } = useLanguage();

  const translations = {
    en: {
      badge: 'Returns & Refunds',
      title: 'Return Policy',
      lastUpdated: 'Last Updated',
      intro:
        'At Penlab Peptide, all products are sold strictly FOR RESEARCH USE ONLY, Not for human consumption. Due to the nature of research peptides and laboratory chemicals, we cannot accept returns or refunds on any opened, used or unsealed products.',
      sections: [
        {
          icon: Clock,
          title: '14-day window for unopened products',
          content:
            'Unopened products may be returned within 14 days of delivery if they are in their original sealed packaging and in resalable condition. Requests submitted after the 14-day window cannot be processed.',
        },
        {
          icon: CheckCircle2,
          title: 'Conditions for a valid return',
          content: `• Product must be unopened and in its original sealed packaging
• Customer is responsible for return shipping costs
• Shipping and handling fees are non-refundable
• All returns are subject to inspection and approval by our compliance team
• Include the original order number and invoice with the return`,
        },
        {
          icon: XCircle,
          title: 'Exclusions, items we cannot accept back',
          content: `• Opened, used, or unsealed products
• Products damaged by the customer
• Products returned after 14 days from delivery
• Custom-synthesised or special-order peptides
• Products without the original order reference`,
        },
        {
          icon: Package,
          title: 'How to request a return',
          content: `1. Email info@penlabpeptide.com with your order number and the reason for the return.
2. Our compliance team will confirm eligibility and issue return instructions within 2 business days.
3. Ship the product in its original sealed packaging using a tracked service, keep the tracking number for your records.
4. Do NOT send returns without prior written authorisation, unauthorised returns are refused at receipt.`,
        },
        {
          icon: RefreshCw,
          title: 'Refund timing',
          content:
            'Once your return is received and inspected, we will process your refund within 7 business days to the original payment method. Bank or card issuer processing may add 3-10 additional business days before the funds appear on your statement.',
        },
      ],
      contact: 'Questions about a return?',
      contactText:
        'Our compliance team is available in English, Spanish and Russian and replies within one business day.',
      email: 'info@penlabpeptide.com',
    },
    es: {
      badge: 'Devoluciones y Reembolsos',
      title: 'Política de Devoluciones',
      lastUpdated: 'Última actualización',
      intro:
        'En Penlab Peptide, todos los productos se venden estrictamente SOLO PARA USO EN INVESTIGACIÓN, No apto para consumo humano. Debido a la naturaleza de los péptidos de investigación y los productos químicos de laboratorio, no podemos aceptar devoluciones ni reembolsos de productos abiertos, usados o sin precinto.',
      sections: [
        {
          icon: Clock,
          title: 'Plazo de 14 días para productos sin abrir',
          content:
            'Los productos sin abrir pueden devolverse dentro de los 14 días posteriores a la entrega si se encuentran en su embalaje original sellado y en condiciones de reventa. Las solicitudes presentadas fuera de este plazo no podrán ser tramitadas.',
        },
        {
          icon: CheckCircle2,
          title: 'Condiciones para una devolución válida',
          content: `• El producto debe estar sin abrir y en su embalaje original sellado
• El cliente asume los gastos de envío de la devolución
• Los gastos de envío y gestión no son reembolsables
• Todas las devoluciones están sujetas a inspección y aprobación de nuestro equipo de cumplimiento
• Incluya el número de pedido y la factura original con la devolución`,
        },
        {
          icon: XCircle,
          title: 'Exclusiones, artículos que no aceptamos',
          content: `• Productos abiertos, usados o sin precinto
• Productos dañados por el cliente
• Productos devueltos tras 14 días desde la entrega
• Péptidos sintetizados a medida o pedidos especiales
• Productos sin la referencia de pedido original`,
        },
        {
          icon: Package,
          title: 'Cómo solicitar una devolución',
          content: `1. Envíe un correo a info@penlabpeptide.com con su número de pedido y el motivo de la devolución.
2. Nuestro equipo de cumplimiento confirmará la elegibilidad y enviará las instrucciones en un plazo de 2 días laborables.
3. Envíe el producto en su embalaje original sellado mediante un servicio con seguimiento, guarde el número de seguimiento.
4. NO envíe devoluciones sin autorización previa por escrito, las devoluciones no autorizadas se rechazan a la recepción.`,
        },
        {
          icon: RefreshCw,
          title: 'Plazos de reembolso',
          content:
            'Una vez recibida e inspeccionada su devolución, procesaremos el reembolso al método de pago original en un plazo de 7 días laborables. Su banco o emisor de la tarjeta puede añadir de 3 a 10 días laborables adicionales hasta que el importe aparezca en su extracto.',
        },
      ],
      contact: '¿Preguntas sobre una devolución?',
      contactText:
        'Nuestro equipo de cumplimiento atiende en inglés, español y ruso y responde en un día laborable.',
      email: 'info@penlabpeptide.com',
    },
    ru: {
      badge: 'Возвраты и возмещения',
      title: 'Политика возврата',
      lastUpdated: 'Последнее обновление',
      intro:
        'В Penlab Peptide все товары продаются строго ТОЛЬКО ДЛЯ ИССЛЕДОВАТЕЛЬСКИХ ЦЕЛЕЙ, не для потребления человеком. В силу природы исследовательских пептидов и лабораторных реактивов мы не принимаем возвраты и не возвращаем средства за открытые, использованные или распечатанные товары.',
      sections: [
        {
          icon: Clock,
          title: '14 дней на возврат нераспечатанных товаров',
          content:
            'Нераспечатанные товары можно вернуть в течение 14 дней с момента получения, если они находятся в оригинальной запечатанной упаковке и пригодны для повторной продажи. Заявки, поданные позже 14 дней, не рассматриваются.',
        },
        {
          icon: CheckCircle2,
          title: 'Условия для оформления возврата',
          content: `• Товар должен быть нераспечатан и в оригинальной запечатанной упаковке
• Расходы на обратную доставку несёт клиент
• Стоимость доставки и обработки не подлежит возмещению
• Все возвраты подлежат проверке и одобрению нашей комиссией
• Приложите оригинальный номер заказа и счёт-фактуру`,
        },
        {
          icon: XCircle,
          title: 'Исключения, что мы не принимаем',
          content: `• Открытые, использованные или распечатанные товары
• Товары, повреждённые клиентом
• Товары, отправленные более чем через 14 дней после получения
• Индивидуально синтезированные или специальные заказы
• Товары без ссылки на оригинальный заказ`,
        },
        {
          icon: Package,
          title: 'Как оформить возврат',
          content: `1. Напишите на info@penlabpeptide.com, указав номер заказа и причину возврата.
2. Наша комиссия подтвердит право на возврат и выдаст инструкции в течение 2 рабочих дней.
3. Отправьте товар в оригинальной запечатанной упаковке службой с отслеживанием, сохраните трек-номер.
4. НЕ отправляйте возврат без письменного разрешения, несогласованные возвраты отклоняются при получении.`,
        },
        {
          icon: RefreshCw,
          title: 'Сроки возмещения',
          content:
            'После получения и проверки возврата мы осуществим возмещение на исходный способ оплаты в течение 7 рабочих дней. Банк или эмитент карты может добавить ещё 3-10 рабочих дней, пока средства не отразятся на выписке.',
        },
      ],
      contact: 'Остались вопросы?',
      contactText:
        'Наша комиссия по соответствию работает на английском, испанском и русском языках и отвечает в течение одного рабочего дня.',
      email: 'info@penlabpeptide.com',
    },
    de: {
      badge: 'Rückgaben & Rückerstattungen',
      title: 'Rückgaberichtlinie',
      lastUpdated: 'Zuletzt aktualisiert',
      intro:
        'Bei Penlab Peptide werden alle Produkte ausschließlich FÜR FORSCHUNGSZWECKE VERKAUFT, Nicht für den menschlichen Verzehr. Aufgrund der Beschaffenheit von Forschungspeptiden und Labormaterialien können wir keine Rückgaben oder Rückerstattungen für geöffnete, verwendete oder entsiegelte Produkte akzeptieren.',
      sections: [
        {
          icon: Clock,
          title: '14-tägiges Zeitfenster für ungeöffnete Produkte',
          content:
            'Ungeöffnete Produkte können innerhalb von 14 Tagen nach Lieferung zurückgegeben werden, sofern sie sich in der originalen, versiegelten Verpackung und in wiederverkäuflichem Zustand befinden. Anträge, die nach Ablauf der 14-tägigen Frist eingereicht werden, können nicht bearbeitet werden.',
        },
        {
          icon: CheckCircle2,
          title: 'Bedingungen für eine gültige Rückgabe',
          content: `• Das Produkt muss ungeöffnet und in seiner originalen, versiegelten Verpackung sein
• Der Kunde ist für die Rücksendekosten verantwortlich
• Versand- und Bearbeitungsgebühren sind nicht erstattungsfähig
• Alle Rücksendungen unterliegen der Prüfung und Genehmigung durch unser Compliance-Team
• Legen Sie der Rücksendung die ursprüngliche Bestellnummer und Rechnung bei`,
        },
        {
          icon: XCircle,
          title: 'Ausschlüsse, Artikel, die wir nicht zurücknehmen können',
          content: `• Geöffnete, verwendete oder entsiegelte Produkte
• Durch den Kunden beschädigte Produkte
• Produkte, die mehr als 14 Tage nach Lieferung zurückgesendet werden
• Kundenspezifisch synthetisierte oder Sonderbestell-Forschungspeptide
• Produkte ohne die ursprüngliche Bestellreferenz`,
        },
        {
          icon: Package,
          title: 'So beantragen Sie eine Rückgabe',
          content: `1. Senden Sie eine E-Mail an info@penlabpeptide.com mit Ihrer Bestellnummer und dem Grund für die Rückgabe.
2. Unser Compliance-Team bestätigt die Anspruchsberechtigung und sendet Ihnen innerhalb von 2 Werktagen Rücksendeanweisungen.
3. Versenden Sie das Produkt in seiner originalen, versiegelten Verpackung mit einem Sendungsverfolgungsdienst, bewahren Sie die Sendungsnummer für Ihre Unterlagen auf.
4. Senden Sie KEINE Rücksendungen ohne vorherige schriftliche Genehmigung, unautorisierte Rücksendungen werden bei Eingang abgelehnt.`,
        },
        {
          icon: RefreshCw,
          title: 'Zeitpunkt der Rückerstattung',
          content:
            'Sobald Ihre Rücksendung eingegangen und geprüft wurde, veranlassen wir innerhalb von 7 Werktagen eine Rückerstattung auf die ursprüngliche Zahlungsmethode. Die Bearbeitung durch Bank oder Kartenaussteller kann zusätzliche 3-10 Werktage in Anspruch nehmen, bevor die Gelder auf Ihrem Kontoauszug erscheinen.',
        },
      ],
      contact: 'Fragen zu einer Rückgabe?',
      contactText:
        'Unser Compliance-Team steht auf Englisch, Spanisch und Russisch zur Verfügung und antwortet innerhalb eines Werktages.',
      email: 'info@penlabpeptide.com',
    },
    fr: {
      badge: 'Retours et remboursements',
      title: 'Politique de retour',
      lastUpdated: 'Dernière mise à jour',
      intro:
        'Chez Penlab Peptide, tous les produits sont vendus strictement POUR UNE UTILISATION EN RECHERCHE UNIQUEMENT, Non destinés à la consommation humaine. En raison de la nature des peptides de recherche et des produits chimiques de laboratoire, nous ne pouvons accepter aucun retour ni remboursement sur les produits ouverts, utilisés ou dont le scellé a été rompu.',
      sections: [
        {
          icon: Clock,
          title: 'Délai de 14 jours pour les produits non ouverts',
          content:
            'Les produits non ouverts peuvent être retournés dans les 14 jours suivant la livraison s’ils sont dans leur emballage d’origine scellé et en état de revente. Les demandes soumises après le délai de 14 jours ne peuvent pas être traitées.',
        },
        {
          icon: CheckCircle2,
          title: 'Conditions pour un retour valide',
          content: `• Le produit doit être non ouvert et dans son emballage d’origine scellé
• Le client est responsable des frais d’expédition de retour
• Les frais d’expédition et de manutention ne sont pas remboursables
• Tous les retours sont soumis à l’inspection et à l’approbation de notre équipe de conformité
• Inclure le numéro de commande initial et la facture avec le retour`,
        },
        {
          icon: XCircle,
          title: 'Exclusions, articles que nous ne pouvons pas reprendre',
          content: `• Produits ouverts, utilisés ou dont le scellé a été rompu
• Produits endommagés par le client
• Produits retournés plus de 14 jours après la livraison
• Peptides synthétisés sur mesure ou commandés spécialement
• Produits sans la référence de commande d’origine`,
        },
        {
          icon: Package,
          title: 'Comment demander un retour',
          content: `1. Envoyez un e-mail à info@penlabpeptide.com avec votre numéro de commande et la raison du retour.
2. Notre équipe de conformité confirmera l’éligibilité et fournira les instructions de retour dans un délai de 2 jours ouvrables.
3. Expédiez le produit dans son emballage d’origine scellé en utilisant un service avec suivi, conservez le numéro de suivi pour vos dossiers.
4. NE PAS envoyer de retour sans autorisation écrite préalable, les retours non autorisés sont refusés à la réception.`,
        },
        {
          icon: RefreshCw,
          title: 'Délais de remboursement',
          content:
            'Une fois votre retour reçu et inspecté, nous traiterons votre remboursement dans un délai de 7 jours ouvrables vers le mode de paiement initial. Le traitement par la banque ou l’émetteur de la carte peut ajouter 3 à 10 jours ouvrables supplémentaires avant que les fonds n’apparaissent sur votre relevé.',
        },
      ],
      contact: 'Questions concernant un retour ?',
      contactText:
        'Notre équipe de conformité est disponible en anglais, espagnol et russe et répond dans un délai d’un jour ouvrable.',
      email: 'info@penlabpeptide.com',
    },
    it: {
      badge: 'Resi e rimborsi',
      title: 'Politica di reso',
      lastUpdated: 'Ultimo aggiornamento',
      intro:
        'Presso Penlab Peptide, tutti i prodotti sono venduti esclusivamente PER SOLO USO DI RICERCA, Non per il consumo umano. Data la natura dei peptidi di ricerca e dei prodotti chimici di laboratorio, non possiamo accettare resi o rimborsi su prodotti aperti, utilizzati o non sigillati.',
      sections: [
        {
          icon: Clock,
          title: 'Finestra di 14 giorni per prodotti non aperti',
          content:
            'I prodotti non aperti possono essere restituiti entro 14 giorni dalla consegna se si trovano nella loro confezione originale sigillata e in condizioni rivendibili. Le richieste inviate dopo il periodo di 14 giorni non possono essere elaborate.',
        },
        {
          icon: CheckCircle2,
          title: 'Condizioni per un reso valido',
          content: `• Il prodotto deve essere non aperto e nella sua confezione originale sigillata
• Il cliente è responsabile dei costi di spedizione per il reso
• Le spese di spedizione e gestione non sono rimborsabili
• Tutti i resi sono soggetti a ispezione e approvazione da parte del nostro team di compliance
• Includere il numero d'ordine originale e la fattura con il reso`,
        },
        {
          icon: XCircle,
          title: 'Esclusioni, articoli che non possiamo accettare',
          content: `• Prodotti aperti, utilizzati o non sigillati
• Prodotti danneggiati dal cliente
• Prodotti restituiti dopo 14 giorni dalla consegna
• Peptidi sintetizzati su misura o speciali su ordinazione
• Prodotti senza il riferimento dell'ordine originale`,
        },
        {
          icon: Package,
          title: 'Come richiedere un reso',
          content: `1. Invia un'email a info@penlabpeptide.com con il tuo numero d'ordine e il motivo del reso.
2. Il nostro team di compliance confermerà l'idoneità e fornirà le istruzioni per il reso entro 2 giorni lavorativi.
3. Spedisci il prodotto nella sua confezione originale sigillata utilizzando un servizio tracciabile, conserva il numero di tracciamento per i tuoi archivi.
4. NON inviare resi senza previa autorizzazione scritta, i resi non autorizzati vengono rifiutati alla ricezione.`,
        },
        {
          icon: RefreshCw,
          title: 'Tempistiche del rimborso',
          content:
            'Una volta ricevuto e ispezionato il tuo reso, elaboreremo il rimborso entro 7 giorni lavorativi sul metodo di pagamento originale. L\'elaborazione da parte della banca o dell\'emittente della carta può aggiungere 3-10 ulteriori giorni lavorativi prima che i fondi compaiano sull\'estratto conto.',
        },
      ],
      contact: 'Domande su un reso?',
      contactText:
        'Il nostro team di compliance è disponibile in inglese, spagnolo e russo e risponde entro un giorno lavorativo.',
      email: 'info@penlabpeptide.com',
    },
    pl: {
      badge: 'Zwroty i refundacje',
      title: 'Polityka zwrotów',
      lastUpdated: 'Ostatnia aktualizacja',
      intro:
        'W Penlab Peptide wszystkie produkty są sprzedawane wyłącznie DO ZASTOSOWAŃ BADAWCZYCH, Nie do spożycia przez ludzi. Ze względu na charakter peptydów badawczych i odczynników laboratoryjnych nie możemy przyjmować zwrotów ani udzielać refundacji za jakiekolwiek otwarte, używane lub naruszone produkty.',
      sections: [
        {
          icon: Clock,
          title: '14-dniowy okres na zwrot nieotwartych produktów',
          content:
            'Nieotwarte produkty mogą zostać zwrócone w ciągu 14 dni od dostawy, jeśli znajdują się w oryginalnym, nienaruszonym opakowaniu i nadają się do ponownej sprzedaży. Wnioski złożone po upływie 14-dniowego okresu nie mogą zostać rozpatrzone.',
        },
        {
          icon: CheckCircle2,
          title: 'Warunki ważnego zwrotu',
          content: `• Produkt musi być nieotwarty i w oryginalnym, nienaruszonym opakowaniu
• Klient ponosi odpowiedzialność za koszty wysyłki zwrotnej
• Opłaty za wysyłkę i obsługę nie podlegają zwrotowi
• Wszystkie zwroty podlegają inspekcji i zatwierdzeniu przez nasz zespół ds. zgodności
• Do zwrotu należy dołączyć oryginalny numer zamówienia i fakturę`,
        },
        {
          icon: XCircle,
          title: 'Wyłączenia, produkty, których nie możemy przyjąć z powrotem',
          content: `• Produkty otwarte, używane lub z naruszonym opakowaniem
• Produkty uszkodzone przez klienta
• Produkty zwrócone po upływie 14 dni od dostawy
• Peptydy syntezowane na zamówienie lub zamówienia specjalne
• Produkty bez oryginalnego numeru zamówienia`,
        },
        {
          icon: Package,
          title: 'Jak złożyć wniosek o zwrot',
          content: `1. Wyślij e-mail na adres info@penlabpeptide.com, podając numer zamówienia oraz powód zwrotu.
2. Nasz zespół ds. zgodności potwierdzi kwalifikowalność i w ciągu 2 dni roboczych przekaże instrukcje dotyczące zwrotu.
3. Wyślij produkt w jego oryginalnym, nienaruszonym opakowaniu, korzystając z usługi z numerem śledzenia, zachowaj numer śledzenia do swojej dokumentacji.
4. NIE wysyłaj zwrotów bez wcześniejszej pisemnej autoryzacji, nieautoryzowane zwroty są odrzucane przy odbiorze.`,
        },
        {
          icon: RefreshCw,
          title: 'Czas realizacji refundacji',
          content:
            'Po otrzymaniu i sprawdzeniu zwrotu, zrealizujemy refundację w ciągu 7 dni roboczych na pierwotną metodę płatności. Przetwarzanie przez bank lub wystawcę karty może wydłużyć ten czas o dodatkowe 3-10 dni roboczych, zanim środki pojawią się na wyciągu.',
        },
      ],
      contact: 'Pytania dotyczące zwrotu?',
      contactText:
        'Nasz zespół ds. zgodności jest dostępny w języku angielskim, hiszpańskim i rosyjskim i odpowiada w ciągu jednego dnia roboczego.',
      email: 'info@penlabpeptide.com',
    },
    nl: {
      badge: 'Retouren & terugbetalingen',
      title: 'Retourbeleid',
      lastUpdated: 'Laatst bijgewerkt',
      intro:
        'Bij Penlab Peptide worden alle producten strikt verkocht VOOR ALLEEN ONDERZOEKS­DOELEINDEN, Niet voor menselijke consumptie. Vanwege de aard van onderzoekspeptiden en laboratoriumchemicaliën kunnen wij geen retouren of terugbetalingen accepteren voor geopende, gebruikte of niet-verzegelde producten.',
      sections: [
        {
          icon: Clock,
          title: 'Termijn van 14 dagen voor ongeopende producten',
          content:
            'Ongeopende producten kunnen binnen 14 dagen na levering worden geretourneerd, mits zij zich in de originele verzegelde verpakking bevinden en verkoopbaar zijn. Verzoeken die na de termijn van 14 dagen worden ingediend, kunnen niet worden verwerkt.',
        },
        {
          icon: CheckCircle2,
          title: 'Voorwaarden voor een geldige retourzending',
          content: `• Product moet ongeopend zijn en in de originele verzegelde verpakking zitten
• Klant is verantwoordelijk voor de retourverzendkosten
• Verzend- en verwerkingskosten worden niet terugbetaald
• Alle retourzendingen zijn onderworpen aan inspectie en goedkeuring door ons compliance-team
• Voeg het oorspronkelijke ordernummer en de factuur bij de retourzending`,
        },
        {
          icon: XCircle,
          title: 'Uitsluitingen, artikelen die we niet kunnen accepteren',
          content: `• Geopende, gebruikte of niet-verzegelde producten
• Producten die door de klant zijn beschadigd
• Producten die later dan 14 dagen na levering worden geretourneerd
• Klantspecifiek gesynthetiseerde of speciaal bestelde peptiden
• Producten zonder de oorspronkelijke orderreferentie`,
        },
        {
          icon: Package,
          title: 'Hoe u een retourzending aanvraagt',
          content: `1. Stuur een e-mail naar info@penlabpeptide.com met uw ordernummer en de reden voor de retourzending.
2. Ons compliance-team zal de geschiktheid bevestigen en binnen 2 werkdagen retourinstructies verstrekken.
3. Verstuur het product in de originele verzegelde verpakking via een zending met tracking, bewaar het trackingnummer voor uw administratie.
4. Verstuur GEEN retourzendingen zonder voorafgaande schriftelijke toestemming, ongeautoriseerde retourzendingen worden bij ontvangst geweigerd.`,
        },
        {
          icon: RefreshCw,
          title: 'Tijdschema voor terugbetaling',
          content:
            'Zodra uw retourzending is ontvangen en geïnspecteerd, verwerken wij uw terugbetaling binnen 7 werkdagen naar de oorspronkelijke betaalmethode. De verwerking door bank of kaartuitgever kan nog 3-10 extra werkdagen duren voordat de middelen op uw afschrift verschijnen.',
        },
      ],
      contact: 'Vragen over een retourzending?',
      contactText:
        'Ons compliance-team is beschikbaar in het Engels, Spaans en Russisch en beantwoordt berichten binnen één werkdag.',
      email: 'info@penlabpeptide.com',
    },
    pt: {
      badge: 'Devoluções e Reembolsos',
      title: 'Política de Devolução',
      lastUpdated: 'Última atualização',
      intro:
        'Na Penlab Peptide, todos os produtos são vendidos ESTRITAMENTE PARA USO EM PESQUISA, Não destinados ao consumo humano. Devido à natureza dos peptídeos de pesquisa e produtos químicos laboratoriais, não podemos aceitar devoluções ou reembolsos de quaisquer produtos abertos, usados ou com o lacre violado.',
      sections: [
        {
          icon: Clock,
          title: 'Prazo de 14 dias para produtos não abertos',
          content:
            'Produtos não abertos podem ser devolvidos em até 14 dias após a entrega, desde que estejam em sua embalagem original lacrada e em condição de revenda. Solicitações enviadas após o prazo de 14 dias não podem ser processadas.',
        },
        {
          icon: CheckCircle2,
          title: 'Condições para uma devolução válida',
          content: `• O produto deve estar não aberto e em sua embalagem original lacrada
• O cliente é responsável pelos custos de envio da devolução
• Taxas de envio e manuseio não são reembolsáveis
• Todas as devoluções estão sujeitas à inspeção e aprovação pela nossa equipe de compliance
• Inclua o número do pedido original e a fatura junto com a devolução`,
        },
        {
          icon: XCircle,
          title: 'Exclusões, itens que não podemos aceitar de volta',
          content: `• Produtos abertos, usados ou com o lacre violado
• Produtos danificados pelo cliente
• Produtos devolvidos após 14 dias da entrega
• Peptídeos sob encomenda ou de síntese personalizada
• Produtos sem a referência do pedido original`,
        },
        {
          icon: Package,
          title: 'Como solicitar uma devolução',
          content: `1. Envie um e-mail para info@penlabpeptide.com com o número do seu pedido e o motivo da devolução.
2. Nossa equipe de compliance confirmará a elegibilidade e enviará as instruções de devolução em até 2 dias úteis.
3. Envie o produto em sua embalagem original lacrada utilizando um serviço com rastreamento, mantenha o número de rastreamento para seus registros.
4. NÃO envie devoluções sem autorização prévia por escrito, devoluções não autorizadas serão recusadas no recebimento.`,
        },
        {
          icon: RefreshCw,
          title: 'Prazo para reembolso',
          content:
            'Assim que sua devolução for recebida e inspecionada, processaremos seu reembolso em até 7 dias úteis para o método de pagamento original. O processamento pelo banco ou emissor do cartão pode adicionar de 3 a 10 dias úteis adicionais antes que os valores apareçam em seu extrato.',
        },
      ],
      contact: 'Dúvidas sobre uma devolução?',
      contactText:
        'Nossa equipe de compliance está disponível em inglês, espanhol e russo e responde em até um dia útil.',
      email: 'info@penlabpeptide.com',
    },
  };

  const t = translations[currentLanguage] || translations.en;

  const iconColors = [
    'from-capsule-teal to-capsule-aqua',
    'from-emerald-500 to-teal-500',
    'from-rose-500 to-red-500',
    'from-indigo-500 to-blue-500',
    'from-amber-500 to-orange-500',
  ];

  return (
    <div className="min-h-screen bg-capsule-bg" data-testid="return-policy-page">
      <SEOHead
        title={`${t.title} | Penlab Peptide`}
        description={t.intro.substring(0, 160)}
        path="/return-policy"
      />
      <Header />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-capsule-teal to-capsule-aqua overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-capsule-teal/90 via-capsule-teal/80 to-transparent" />
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl" />
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <RefreshCw className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t.badge}</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t.title}
            </h1>
            <p className="text-lg text-gray-300">
              {t.lastUpdated}: April 2026
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Intro callout, repeats the research-use disclaimer so compliance
            reviewers (and Google Merchant Center) see it above the fold. */}
        <div className="glass-card rounded-3xl p-8 mb-8" data-testid="return-policy-intro">
          <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">{t.intro}</p>
        </div>

        <div className="space-y-6">
          {t.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <div
                key={idx}
                className="glass-card rounded-3xl p-8 hover:shadow-glass-hover transition-all duration-300"
                data-testid={`return-policy-section-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br ${iconColors[idx % iconColors.length]} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-3xl shadow-teal-glow p-8 mt-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">{t.contact}</h2>
          <p className="mb-6 opacity-90">{t.contactText}</p>
          <a
            href={`mailto:${t.email}`}
            className="inline-flex items-center gap-2 bg-white text-capsule-teal px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            data-testid="return-policy-contact-email"
          >
            <Mail className="w-5 h-5" />
            {t.email}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReturnPolicyPage;
